
import { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { Application } from '@/types/database.types';

interface RiskAssessmentQuestionsProps {
  register: UseFormRegister<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  onFormChange: () => void;
}

export const RiskAssessmentQuestions = ({
  register,
  setValue,
  watch,
  onFormChange,
}: RiskAssessmentQuestionsProps) => {
  const hasSafetyProgram = watch('has_safety_program') as boolean | undefined;
  const hasElevatedRisk = watch('has_elevated_workplace_risk') as boolean | undefined;
  const hasCannabisContact = watch('has_cannabis_hemp_contact') as boolean | undefined;

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium mb-4">Risk Assessment</h3>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="has_safety_program" className="flex-1">
            Does the organization have an established safety program?
          </Label>
          <Switch
            id="has_safety_program"
            checked={hasSafetyProgram || false}
            onCheckedChange={(checked) => {
              setValue('has_safety_program', checked);
              onFormChange();
            }}
          />
        </div>
        {hasSafetyProgram && (
          <div className="space-y-2 pl-5 border-l-2 border-primary/20">
            <Label htmlFor="safety_program_details">
              Please describe the safety program, including key components, training frequency, and safety protocols
            </Label>
            <Textarea
              id="safety_program_details"
              {...register('safety_program_details')}
              placeholder="Describe the safety program in detail..."
              className="min-h-[100px]"
            />
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="has_over_road_trucking" className="flex-1">
            Does the organization operate over-the-road trucking?
          </Label>
          <Switch
            id="has_over_road_trucking"
            checked={watch('has_over_road_trucking') as boolean | undefined || false}
            onCheckedChange={(checked) => {
              setValue('has_over_road_trucking', checked);
              onFormChange();
            }}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="has_cannabis_hemp_contact" className="flex-1">
            Does the organization have any contact with cannabis or hemp products?
          </Label>
          <Switch
            id="has_cannabis_hemp_contact"
            checked={hasCannabisContact || false}
            onCheckedChange={(checked) => {
              setValue('has_cannabis_hemp_contact', checked);
              onFormChange();
            }}
          />
        </div>
        {hasCannabisContact && (
          <div className="space-y-2 pl-5 border-l-2 border-primary/20">
            <Label htmlFor="cannabis_hemp_details">Please describe the nature of contact with cannabis/hemp</Label>
            <Textarea
              id="cannabis_hemp_details"
              {...register('cannabis_hemp_details')}
              placeholder="Describe the nature and extent of cannabis/hemp contact..."
              className="min-h-[100px]"
            />
          </div>
        )}

        <div className="flex items-center justify-between">
          <Label htmlFor="has_elevated_workplace_risk" className="flex-1">
            Are there any elevated workplace risks?
          </Label>
          <Switch
            id="has_elevated_workplace_risk"
            checked={hasElevatedRisk || false}
            onCheckedChange={(checked) => {
              setValue('has_elevated_workplace_risk', checked);
              onFormChange();
            }}
          />
        </div>
        {hasElevatedRisk && (
          <div className="space-y-2 pl-5 border-l-2 border-primary/20">
            <Label htmlFor="workplace_risk_details">Please describe the elevated workplace risks</Label>
            <Textarea
              id="workplace_risk_details"
              {...register('workplace_risk_details')}
              placeholder="Describe the elevated workplace risks..."
              className="min-h-[100px]"
            />
          </div>
        )}

        <div className="flex items-center justify-between">
          <Label htmlFor="has_company_aircraft" className="flex-1">
            Does the organization operate aircraft?
          </Label>
          <Switch
            id="has_company_aircraft"
            checked={watch('has_company_aircraft') as boolean | undefined || false}
            onCheckedChange={(checked) => {
              setValue('has_company_aircraft', checked);
              onFormChange();
            }}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="has_marine_operations" className="flex-1">
            Does the organization have marine operations?
          </Label>
          <Switch
            id="has_marine_operations"
            checked={watch('has_marine_operations') as boolean | undefined || false}
            onCheckedChange={(checked) => {
              setValue('has_marine_operations', checked);
              onFormChange();
            }}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="has_heavy_machinery" className="flex-1">
            Does the organization use heavy machinery?
          </Label>
          <Switch
            id="has_heavy_machinery"
            checked={watch('has_heavy_machinery') as boolean | undefined || false}
            onCheckedChange={(checked) => {
              setValue('has_heavy_machinery', checked);
              onFormChange();
            }}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="has_recent_wc_claims" className="flex-1">
            Have there been any workers compensation claims in the past 5 years?
          </Label>
          <Switch
            id="has_recent_wc_claims"
            checked={watch('has_recent_wc_claims') as boolean | undefined || false}
            onCheckedChange={(checked) => {
              setValue('has_recent_wc_claims', checked);
              onFormChange();
            }}
          />
        </div>
      </div>
    </div>
  );
};
